import React, { useState, useRef, useMemo } from "react";
import Button from "../components/button/Button";
import InputField from "../components/textfields/InputField";
import axios from "axios";
import { saveAs } from "file-saver";
import { 
  MagnifyingGlassIcon, 
  ArrowDownTrayIcon, 
  ArrowUpTrayIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  ChevronUpDownIcon,
  FunnelIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  XCircleIcon,
  InformationCircleIcon
} from "@heroicons/react/24/outline";

const BulkTracking = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingData, setTrackingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef(null);

  // Status badge configuration
  const getStatusBadge = (status) => {
    const statusConfig = {
      "Delivered": { 
        color: "bg-green-100 text-green-800 border-green-200",
        icon: CheckCircleIcon
      },
      "In Transit": { 
        color: "bg-blue-100 text-blue-800 border-blue-200",
        icon: ClockIcon
      },
      "Out for Delivery": { 
        color: "bg-purple-100 text-purple-800 border-purple-200",
        icon: ClockIcon
      },
      "Pending": { 
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: ExclamationCircleIcon
      },
      "Failed": { 
        color: "bg-red-100 text-red-800 border-red-200",
        icon: XCircleIcon
      },
      "Cancelled": { 
        color: "bg-gray-100 text-gray-800 border-gray-200",
        icon: XCircleIcon
      }
    };
    
    const config = statusConfig[status] || { 
      color: "bg-gray-100 text-gray-800 border-gray-200",
      icon: InformationCircleIcon
    };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.color} transition-all duration-200 hover:shadow-md`}>
        <Icon className="w-4 h-4" />
        {status}
      </span>
    );
  };

  // Payment method badge
  const getPaymentBadge = (method) => {
    const config = method === "COD" 
      ? "bg-orange-100 text-orange-800 border-orange-200"
      : "bg-indigo-100 text-indigo-800 border-indigo-200";
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${config}`}>
        {method}
      </span>
    );
  };

  // Parse CSV file
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line);
    const awbNumbers = [];
    
    // Try to detect if first line is header
    const firstLine = lines[0].toLowerCase();
    const startIndex = firstLine.includes('awb') || firstLine.includes('tracking') ? 1 : 0;
    
    for (let i = startIndex; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/['"]/g, ''));
      // Take first non-empty value from each line
      const awb = values.find(v => v);
      if (awb) awbNumbers.push(awb);
    }
    
    return awbNumbers;
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      setError("Please upload a valid CSV file");
      return;
    }
    
    setUploadedFileName(file.name);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const awbNumbers = parseCSV(text);
        
        if (awbNumbers.length === 0) {
          setError("No AWB numbers found in the CSV file");
          return;
        }
        
        setTrackingNumber(awbNumbers.join(", "));
        setError("");
      } catch (err) {
        setError("Failed to parse CSV file. Please check the format.");
      }
    };
    
    reader.onerror = () => {
      setError("Failed to read the file");
    };
    
    reader.readAsText(file);
  };

  const handleTrackOrder = async () => {
    if (!trackingNumber.trim()) {
      setError("Please enter AWB numbers or upload a CSV file.");
      return;
    }
    setError("");
    setLoading(true);

    const awbNumbersArray = trackingNumber
      .split(",")
      .map(num => num.trim())
      .filter(num => num !== "");

    try {
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_BULK_TRACKING_URL}`;
      const response = await axios.post(
        apiUrl,
        {
          awb_numbers: awbNumbersArray,
          order_reference_numbers: [],
        },
        {
          headers: {
            Authorization: localStorage.getItem("authToken"),
            "Content-Type": "application/json",
            "x-merchant-id": localStorage.getItem("merchantId"),
          },
        }
      );
      setTrackingData(response.data);
      setFilteredData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch tracking data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Download CSV
  const handleDownloadCSV = () => {
    const headers = [
      "Order Reference Number",
      "Order Date",
      "AWB Number",
      "Courier Partner",
      "Pickup Location",
      "Delivery Location",
      "Charged Weight",
      "Payment Amount",
      "Payment Method",
      "Order Status",
    ];

    const csvRows = filteredData.map((item) => [
      item.orderReferenceNumber || "N/A",
      item.orderDate || "N/A",
      item.awbNumber || "N/A",
      item.courierPartner || "N/A",
      item.pickupLocation || "N/A",
      item.deliveryLocation || "N/A",
      item.chargedWeight || "N/A",
      item.paymentAmount || "N/A",
      item.paymentMethod || "N/A",
      item.orderStatus || "N/A",
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map(cell => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `tracking_data_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  // Clear input
  const handleClear = () => {
    setTrackingNumber("");
    setUploadedFileName("");
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply filters and sorting
  useMemo(() => {
    let result = [...trackingData];

    // Search filter
    if (searchTerm) {
      result = result.filter(item => 
        Object.values(item).some(val => 
          String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Status filter
    if (filterStatus !== "all") {
      result = result.filter(item => item.orderStatus === filterStatus);
    }

    // Payment method filter
    if (filterPaymentMethod !== "all") {
      result = result.filter(item => item.paymentMethod === filterPaymentMethod);
    }

    // Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key] || "";
        const bVal = b[sortConfig.key] || "";
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(result);
  }, [trackingData, searchTerm, filterStatus, filterPaymentMethod, sortConfig]);

  // Statistics
  const stats = useMemo(() => {
    const total = trackingData.length;
    const delivered = trackingData.filter(item => item.orderStatus === "Delivered").length;
    const inTransit = trackingData.filter(item => item.orderStatus === "In Transit").length;
    const totalAmount = trackingData.reduce((sum, item) => sum + (parseFloat(item.paymentAmount) || 0), 0);
    
    return { total, delivered, inTransit, totalAmount };
  }, [trackingData]);

  // Get unique statuses and payment methods for filters
  const uniqueStatuses = [...new Set(trackingData.map(item => item.orderStatus))];
  const uniquePaymentMethods = [...new Set(trackingData.map(item => item.paymentMethod))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 mt-2">
      <div className="max-w-full mx-auto space-y-6 px-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <MagnifyingGlassIcon className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
              Bulk Shipment Tracking
            </h3>
            <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Track multiple shipments in real-time with advanced filtering</p>
          </div>
        </div>

        {/* Input Section */}
        <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
          <div className="p-6 md:p-8 space-y-6">
            {/* Input Methods Toggle */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* Manual Input */}
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter AWB Numbers
                </label>
                <div className="relative">
                  <InputField
                    id="trackingNumber"
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter AWB numbers separated by commas (e.g., 123456, 789012, 345678)"
                    className="w-full pr-10"
                  />
                  {trackingNumber && (
                    <button
                      onClick={handleClear}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* File Upload */}
              <div className="md:w-72">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Or Upload CSV File
                </label>
                <div className="relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-200"
                  >
                    <ArrowUpTrayIcon className="w-5 h-5 text-gray-500" />
                    <span className="text-sm text-gray-600 font-medium">
                      {uploadedFileName || "Choose CSV file"}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* File Format Info */}
            <div className="flex items-start gap-2 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <strong>CSV Format:</strong> Upload a CSV file with AWB numbers in the first column. 
                Header row is optional. Example: <code className="bg-blue-100 px-1.5 py-0.5 rounded">31049526698264</code>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-xl animate-shake">
                <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                text={loading ? "Tracking..." : "Track Shipments"}
                className={`bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                onClick={handleTrackOrder}
                disabled={loading}
              />
              <Button
                text="Download Sample CSV"
                className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                onClick={() => {
                  const sample = "AWB Number\n31049526698264\n31049526698275\n31049526698286";
                  const blob = new Blob([sample], { type: "text/csv" });
                  saveAs(blob, "sample_awb_upload.csv");
                }}
              />
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        {trackingData.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs md:text-sm font-medium">Total Shipments</p>
                  <p className="text-2xl md:text-3xl font-bold mt-1">{stats.total}</p>
                </div>
                <DocumentTextIcon className="w-8 h-8 md:w-12 md:h-12 text-blue-200 opacity-80" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs md:text-sm font-medium">Delivered</p>
                  <p className="text-2xl md:text-3xl font-bold mt-1">{stats.delivered}</p>
                </div>
                <CheckCircleIcon className="w-8 h-8 md:w-12 md:h-12 text-green-200 opacity-80" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs md:text-sm font-medium">In Transit</p>
                  <p className="text-2xl md:text-3xl font-bold mt-1">{stats.inTransit}</p>
                </div>
                <ClockIcon className="w-8 h-8 md:w-12 md:h-12 text-purple-200 opacity-80" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-xs md:text-sm font-medium">Total Amount</p>
                  <p className="text-2xl md:text-3xl font-bold mt-1">₹{stats.totalAmount.toFixed(2)}</p>
                </div>
                <div className="text-2xl md:text-4xl opacity-80">₹</div>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {trackingData.length > 0 && (
          <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
            {/* Toolbar */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by AWB, order ref, courier..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      showFilters 
                        ? 'bg-blue-600 text-white shadow-lg' 
                        : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-blue-500'
                    }`}
                  >
                    <FunnelIcon className="w-5 h-5" />
                    Filters
                  </button>
                  <Button
                    text={`Export ${filteredData.length} Records`}
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
                    onClick={handleDownloadCSV}
                  />
                </div>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Order Status
                      </label>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">All Statuses</option>
                        {uniqueStatuses.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Payment Method
                      </label>
                      <select
                        value={filterPaymentMethod}
                        onChange={(e) => setFilterPaymentMethod(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">All Methods</option>
                        {uniquePaymentMethods.map(method => (
                          <option key={method} value={method}>{method}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setFilterStatus("all");
                        setFilterPaymentMethod("all");
                        setSearchTerm("");
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear All Filters
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px]">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-200">
                  <tr>
                    {[
                      { key: 'orderReferenceNumber', label: 'Order Ref' },
                      { key: 'awbNumber', label: 'AWB' },
                      { key: 'orderDate', label: 'Date' },
                      { key: 'courierPartner', label: 'Courier' },
                      { key: 'pickupLocation', label: 'Pickup' },
                      { key: 'deliveryLocation', label: 'Delivery' },
                      { key: 'chargedWeight', label: 'Weight' },
                      { key: 'paymentAmount', label: 'Amount' },
                      { key: 'paymentMethod', label: 'Payment' },
                      { key: 'orderStatus', label: 'Status' }
                    ].map(({ key, label }) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors group whitespace-nowrap"
                      >
                        <div className="flex items-center gap-1">
                          {label}
                          <ChevronUpDownIcon className="w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                      <tr 
                        key={index}
                        className="hover:bg-blue-50 transition-colors group"
                      >
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-medium text-gray-900">
                              {item.orderReferenceNumber || "N/A"}
                            </span>
                            <button
                              onClick={() => copyToClipboard(item.orderReferenceNumber)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Copy to clipboard"
                            >
                              <ClipboardDocumentIcon className="w-3 h-3 text-gray-400 hover:text-blue-600" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-mono font-semibold text-blue-600">
                              {item.awbNumber || "N/A"}
                            </span>
                            <button
                              onClick={() => copyToClipboard(item.awbNumber)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Copy to clipboard"
                            >
                              <ClipboardDocumentIcon className="w-3 h-3 text-gray-400 hover:text-blue-600" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {new Date(item.orderDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-xs font-semibold text-gray-900">
                            {item.courierPartner || "N/A"}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {item.pickupLocation || "N/A"}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                          {item.deliveryLocation || "N/A"}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600 font-medium">
                          {item.chargedWeight || "N/A"}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-xs font-bold text-gray-900">
                            ₹{item.paymentAmount || "0.00"}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {getPaymentBadge(item.paymentMethod || "N/A")}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {getStatusBadge(item.orderStatus || "N/A")}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="10"
                        className="px-6 py-16 text-center"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <MagnifyingGlassIcon className="w-16 h-16 text-gray-300" />
                          <p className="text-gray-500 font-medium">
                            No shipments found matching your criteria
                          </p>
                          <button
                            onClick={() => {
                              setSearchTerm("");
                              setFilterStatus("all");
                              setFilterPaymentMethod("all");
                            }}
                            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                          >
                            Clear all filters
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Results Count */}
            {filteredData.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-900">{filteredData.length}</span> of{" "}
                  <span className="font-semibold text-gray-900">{trackingData.length}</span> shipments
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && trackingData.length === 0 && (
          <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 p-12">
            <div className="flex flex-col items-center gap-4 text-center">
              <DocumentTextIcon className="w-20 h-20 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-900">No Shipments Tracked Yet</h3>
              <p className="text-gray-600 max-w-md">
                Enter AWB numbers manually or upload a CSV file to start tracking your shipments in bulk
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl border border-gray-200 p-12">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
              <p className="text-gray-600 font-medium">Fetching tracking information...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkTracking;
